import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import Backdrop from '../../backdrop/Backdrop';
import { Box, Button, Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { navigate, useParams } from '@reach/router';
import colors from '../../../themes/main-colors';
import { useInstallerJobDetailsQuery } from '../../../hooks/pge-plus/useInstallerJobDetailsQuery';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import InstallerPageHeader from '../../pge-plus-common/InstallerPageHeader';
import InstallerDateAndTime from '../installer-common/InstallerDateAndTime';
import useQuote from '../confirm-cost/useQuote';
import QuoteCustomerInfo from '../installer-common/quote-customer-info';
import {
  ApplicationEv,
  ApplicationEvSectionType,
  InstallSchedulePreference,
  LineItemCost,
  LineItemDetail,
  LineItemProduct,
  QuoteDataEv,
  QuoteDocument,
  SectionEvInstallData,
  SectionEvRebates,
} from '../../../__generated__/pgeplus-types';
import JobInstallationCostRequote from '../installer-common/jobInstallationCostRequote';
import QuoteTotalSection from '../installer-common/quoteTotalSection';
import { JobQuoteModal } from '../confirm-cost/QuoteForm.rules';
import { FormState } from '../../../hooks/useFormState.types';
import { getTimeRangeString } from '../../../util/date-utils';
import { LINE_ITEM_KEY } from '../quote.types';
import ROUTES from '../../../routes';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import UploadQuote from '../installer-common/UploadQuote';
import useUtil from '../../../hooks/pge-plus/useUtil';

function getDefaultJobValues(
  rebateSection: SectionEvRebates,
  lineItemDetailList: LineItemDetail[],
  lineItemCostList: LineItemCost[],
  lineItemProductList: LineItemProduct[],
  jobTypeValue: string,
  chargerModalValue: string,
  content: PageTextContentType,
  defaultFormState: FormState<JobQuoteModal>,
  schedulePreference: InstallSchedulePreference,
): JobQuoteModal {
  return {
    ...defaultFormState.values,
    isCreateQuote: true,
    jobType: jobTypeValue,
    totalInstallationMaterialCost:
      (lineItemCostList &&
        lineItemCostList.filter(
          item => item.key === LINE_ITEM_KEY.INSTALLATION_MATERIALS,
        )[0].amount) ||
      0,
    meterBase:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.METER_BASE)[0]
        .amount.toFixed(2),
    weatherHead:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.WEATHER_HEAD)[0]
        .amount.toFixed(2),
    electricBreaker:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.ELECTRICAL_BREAKER)[0]
        .amount.toFixed(2),
    miscMaterial:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MISC_MATERIALS)[0]
        .amount.toFixed(2),
    labor:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.LABOR)[0]
        .amount.toFixed(2),
    material:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MATERIALS)[0]
        .amount.toFixed(2),
    permitCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.PERMITS)[0]
        .amount.toFixed(2),
    travelCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.TRAVEL_TIME)[0]
        .amount.toFixed(2),
    feesCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.FEES)[0]
        .amount.toFixed(2),
    miscellaneousWorkCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MISCELLANEOUS_WORK)[0]
        .amount.toFixed(2),
    additionalComment:
      lineItemDetailList &&
      lineItemDetailList.filter(item => item.key === LINE_ITEM_KEY.COMMENTS)[0]
        .value,
    chargerCost:
      lineItemProductList &&
      lineItemProductList
        ?.filter(item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL)[0]
        .amount.toFixed(2),
    amperage:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_AMPERAGE,
      )[0].value,
    isReplace:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_UPGRADE,
      )[0].value,
    isDistance:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item =>
          item.key === LINE_ITEM_KEY.CHARGER_INSTALL_WITHIN_PANEL_DISTANCE,
      )[0].value,
    cardinalDirections:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION_CARDINAL_DIRECTION,
      )[0].value,
    outsidePanelLocation:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION,
      )[0].value,
    distance:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PREFERRED_INSTALL_DISTANCE,
      )[0].value,
    panelLocation:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION_MAIN_BREAKER,
      )[0].value,
    rebateEligibility:
      rebateSection && rebateSection?.data?.rebateEligibility?.eligible
        ? content.get('REUSABLE_YES')
        : content.get('REUSABLE_NO'),
    chargerModel: chargerModalValue,
    preferedDay: schedulePreference?.dayOfWeek?.join(', '),
    preferedTime: `${schedulePreference?.preferredServiceTime &&
      schedulePreference?.preferredServiceTime?.[0]} (${
      schedulePreference?.preferredServiceTime
    })`,
    adminFee:
      (lineItemCostList &&
        lineItemCostList.filter(
          item => item.key === LINE_ITEM_KEY.ADMIN_FEE,
        )?.[0]?.amount) ||
      undefined,
  };
}

export const ReQuote = () => {
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_COMPLETE_QUOTE,
  );

  const theme = useTheme();
  const params = useParams();
  const { wrapWithLoader } = useWrapWithLoader();
  const { loading, data, error } = useInstallerJobDetailsQuery(params?.jobId);

  const jobData = data?.getJob.job;
  const applicationData = data?.getJob.application as ApplicationEv;
  const [isPanelUpgrade, setIsPanelUpgrade] = useState<boolean>();
  const [isUploadingQuote, setIsUploadingQuote] = useState<boolean>(false);

  const {
    totalInstallationMaterialCost,
    totalInstallation,
    totalCost,
    costBeforeRebate,
    isUpdate,
    rebateAmount,
    panelRebateAmount,
    jobQuoteForm,
    resetExpandFormData,
    calculateTotalAmount,
    rebateCalculate,
    chargerModelList,
    setChargerModelList,
    submitCreateQuote,
    isCalculatingRebates,
  } = useQuote(content);
  const { setErrorNotification } = useUtil();

  useEffect(() => {
    if (jobData) {
      const chargerModalValue =
        data?.getJob.job.quote &&
        (data?.getJob.job.quote?.data?.lineItems?.filter(
          item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
        )[0] as LineItemProduct).value;
      const jobTypeValue =
        data?.getJob.job.quote &&
        (data?.getJob.job.quote.data.lineItems?.filter(
          (item: any) => item.key === 'JOB_TYPE',
        )[0] as any).value;
      const rebateSection = data?.getJob.application.details?.filter(
        detail => detail.sectionType === 'EV_REBATES',
      )[0] as SectionEvRebates;
      const lineItemDetailList = data?.getJob?.job?.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemDetail',
      ) as LineItemDetail[];
      const lineItemCostList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemCost',
      ) as LineItemCost[];
      const lineItemProductList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemProduct',
      ) as LineItemProduct[];

      const schedulePreference = (data?.getJob?.application?.details?.find(
        detail => detail.sectionType === ApplicationEvSectionType.EvInstall,
      )?.data as SectionEvInstallData)
        .schedulePreference as InstallSchedulePreference;

      jobQuoteForm.reset(
        getDefaultJobValues(
          rebateSection,
          lineItemDetailList,
          lineItemCostList,
          lineItemProductList,
          jobTypeValue,
          chargerModalValue,
          content,
          jobQuoteForm,
          schedulePreference,
        ),
      );

      calculateTotalAmount(false, {
        totalInstallationMaterial:
          (lineItemCostList &&
            lineItemCostList.filter(
              item => item.key === LINE_ITEM_KEY.INSTALLATION_MATERIALS,
            )[0].amount) ||
          0,
        chargerCost:
          lineItemProductList &&
          lineItemProductList?.filter(
            item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
          )[0].amount,
        meterBase:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.METER_BASE,
          )[0].amount,
        weatherHead:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.WEATHER_HEAD,
          )[0].amount,
        electricBreaker:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.ELECTRICAL_BREAKER,
          )[0].amount,
        miscMaterial:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MISC_MATERIALS,
          )[0].amount,
        labor:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.LABOR)[0]
            .amount,
        material:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MATERIALS,
          )[0].amount,
        permitCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.PERMITS)[0]
            .amount,
        travelCost:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.TRAVEL_TIME,
          )[0].amount,
        feesCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.FEES)[0]
            .amount,
        miscellaneousWorkCost:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MISCELLANEOUS_WORK,
          )[0].amount,
      });
    }
  }, [applicationData]);

  useEffect(() => {
    if (!(jobData?.quote?.data as QuoteDataEv)?.declineEnrollment) {
      void rebateCalculate(applicationData);
    }
  }, [
    applicationData,
    isPanelUpgrade,
    isUpdate,
    jobData,
    jobQuoteForm.values.amperage,
  ]);

  const horizontalLine = {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  };

  return (
    <Fragment>
      {loading && <Backdrop forceOpen />}
      <Box
        id="print_section"
        display="flex"
        flexDirection="column"
        mt={1}
        css={{
          color: colors.noirBlurLight,
          maxWidth: '706px',
          margin: 'auto',
          justifyContent: 'center',
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <InstallerPageHeader
              pageHeaderInfo={{
                pageTitle: content.get('REQUOTE_JOB') || '',
                pageSubTitle:
                  content.get('SUBMIT_NEW_QUOTE_FOR_THIS_JOB') || '',
                downloadPackageTitle: content.get('DOWNLOAD_LEAD_PACKAGE'),
              }}
              pendingReasonInfo={{
                jobStatus: jobData?.status || '',
                successTitle: content
                  .get('QUOTE_WAS_SUCCESSFULLY_SENT')
                  ?.replace(
                    '{{date}}',
                    `${jobData?.createdAt &&
                      moment(jobData?.createdAt).format('DD/MM/YYYY')}`,
                  ),
                downloadPackageTitle: content.get('DOWNLOAD_LEAD_PACKAGE'),
              }}
              jobData={data?.getJob!}
              content={content}
            />
            <QuoteCustomerInfo
              content={content}
              applicationNo={applicationData?.applicationNo}
              jobData={jobData?.jobLocation!}
            />
            <hr css={{ ...horizontalLine }} />
            <JobInstallationCostRequote
              content={content}
              applicationData={applicationData}
              title={content.get('COMPLETE_FORM_TO_GENERATE_QUOTE')}
              resetExpandFormData={resetExpandFormData}
              calculateTotalAmount={calculateTotalAmount}
              jobQuoteForm={jobQuoteForm}
              setIsPanelUpgrade={setIsPanelUpgrade}
              panelRebateAmount={panelRebateAmount}
              totalInstallationMaterialCost={totalInstallationMaterialCost}
              totalInstallationCost={totalInstallation}
              chargerModelList={chargerModelList}
              setChargerModelList={setChargerModelList}
            />
            <hr css={{ ...horizontalLine }} />
            <QuoteTotalSection
              content={content}
              rebateAmount={rebateAmount}
              totalCost={totalCost}
              costBeforeRebate={costBeforeRebate}
              isCalculatingRebates={isCalculatingRebates}
            />
            <hr css={{ ...horizontalLine }} />
            <UploadQuote
              content={content}
              jobQuoteForm={jobQuoteForm}
              setIsUploadingQuote={setIsUploadingQuote}
            />
          </Grid>
          <Grid
            item
            xs={12}
            css={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              marginTop: '38px',
            }}
            data-print-exclude-requote
          >
            <Button
              onClick={() =>
                navigate(ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING)
              }
              css={{
                border: `2px solid ${colors.sparkBlue}`,
                padding: '6px 61px',
                borderRadius: '30px',
                color: colors.sparkBlue,
                fontSize: '18px',
                letterSpacing: '0.36px',
              }}
            >
              {content.get('REUSABLE_CANCEL')}
            </Button>
            <Button
              onClick={jobQuoteForm.submit(async values => {
                try {
                  await submitCreateQuote(
                    params?.jobId,
                    jobData?.applicationNo!,
                    ROUTES.PGE_PLUS_INSTALLER_PORTAL_REQUOTE_SUCCESS.replace(
                      ':jobId',
                      params?.jobId,
                    ),
                  );
                } catch (e) {
                  setErrorNotification(true);
                }
              })}
              css={{
                padding: '6px 66px',
                borderRadius: '30px',
                backgroundColor: colors.orange,
                color: colors.lightGray2,
                fontSize: '18px',
                letterSpacing: '0.36px',
                '&:hover': {
                  backgroundColor: colors.orange,
                },
              }}
              disabled={isCalculatingRebates || isUploadingQuote}
            >
              {content.get('SUBMIT')}
            </Button>
          </Grid>
          <hr css={{ ...horizontalLine, marginTop: '100px' }} />
        </Grid>
      </Box>
    </Fragment>
  );
};
